// 施工箇所(ConstructionPlanArea)の状態・進捗・数量集計の純粋関数群。
// モックアップ statusMeta.ts / extendedStore.ts の area 系ヘルパーを Web の正規化スキーマへ移植。
//   - 完了判定は ProcessExecution.completed===true 由来の AreaProcessCompletion を使う(deriveAreaProcessCompletions)。
//   - モックの area.planned_main_qty[work_detail_id] は Web では ConstructionPlanAreaPlannedMaterial(material_id 単位)へ正規化されているため、
//     工程→配合比→material_id→計画材料 で対応付ける。
import type { AreaProcessCompletion } from "../types/derivedSelectors";

// ===== 場所区分スロット(モック area.location_slots の Web 正規化版) =====
// Web は ConstructionPlanAreaLocation(construction_plan_area_id / slot / location_division_id)へ正規化済み。

type AreaLocationLike = { construction_plan_area_id: string; location_division_id: number; slot: number; deleted_at: number | null };
type LocationDivisionLike = { id: number; division_name: string };

// area の場所区分スロットを slot 昇順で返す。
export function getAreaSlots(
  areaId: string,
  locations: AreaLocationLike[],
): AreaLocationLike[] {
  return locations
    .filter((l) => l.deleted_at == null && l.construction_plan_area_id === areaId)
    .sort((a, b) => a.slot - b.slot);
}

// area の場所区分を「区分名 / 区分名 / …」のテキストにする。未設定は空文字。
export function slotsToText(
  areaId: string,
  locations: AreaLocationLike[],
  locationDivisions: LocationDivisionLike[],
): string {
  const slots = getAreaSlots(areaId, locations);
  const names: string[] = [];
  for (const s of slots) {
    const div = locationDivisions.find((d) => d.id === s.location_division_id);
    if (div !== undefined) {
      names.push(div.division_name);
    }
  }
  return names.join(" / ");
}

// ===== 状態 → 表示プロパティ(statusMeta 移植) =====

export type AreaStatus = "完了" | "進行中" | "未着手" | "未完了";

// 施工状態 → MUI Chip 色。"未完了"(下書き)と"進行中"はともに warning。
export const AREA_STATUS_COLOR: Record<AreaStatus, "success" | "warning" | "default"> = {
  "完了": "success",
  "進行中": "warning",
  "未着手": "default",
  "未完了": "warning",
};

// 完了数/全数 → 進捗バー色。success=全完了 / info=一部完了 / warning=未着手。
// total===0(工程未登録)は呼び出し側で別表示する。
export function progressColor(completed: number, total: number): "success" | "info" | "warning" {
  if (completed >= total) {
    return "success";
  }
  if (completed > 0) {
    return "info";
  }
  return "warning";
}

// ===== 入力で受け取る最小スライス型(疎結合のため構造的に定義) =====

type AreaLike = {
  id: string;
  work_subtype_id: number | null;
  planned_qty: number | null;
};
type WorkDetailLike = { id: number; work_subtype_id: number; mix_ratio_definition_id: number | null; name: string; deleted_at: number | null };
type WorkSubtypeLike = { id: number; name: string; unit: string; primary_work_detail_id: number | null; deleted_at: number | null };
type MixRatioDefLike = { id: number; material_id: number; deleted_at: number | null };
type MaterialLike = { id: number; standard_usage: number | null; standard_usage_unit: string | null };
type PlannedMaterialLike = { construction_plan_area_id: string; material_id: number; work_detail_id?: number | null; planned_qty: number; unit: string; deleted_at: number | null };

export function plannedMainQtyForWorkDetail(
  areaId: string,
  materialId: number,
  workDetailId: number,
  plannedMaterials: PlannedMaterialLike[],
): number {
  const directRows = plannedMaterials.filter((p) =>
    p.deleted_at == null &&
    p.construction_plan_area_id === areaId &&
    p.material_id === materialId &&
    (p.work_detail_id ?? null) === workDetailId
  );
  if (directRows.length > 0) {
    return directRows.reduce((sum, p) => sum + p.planned_qty, 0);
  }
  return plannedMaterials
    .filter((p) =>
      p.deleted_at == null &&
      p.construction_plan_area_id === areaId &&
      p.material_id === materialId &&
      (p.work_detail_id ?? null) === null
    )
    .reduce((sum, p) => sum + p.planned_qty, 0);
}

// 種別配下の未削除工程を id 昇順で返す。
function workDetailsOfSubtype(workDetails: WorkDetailLike[], subtypeId: number | null): WorkDetailLike[] {
  if (subtypeId == null) {
    return [];
  }
  return workDetails
    .filter((d) => d.deleted_at == null && d.work_subtype_id === subtypeId)
    .sort((a, b) => a.id - b.id);
}

// ===== 施工箇所の状態 =====

// 完了=全工程完了 / 進行中=一部完了 / 未着手=完了0 or 工程未登録。
export function getAreaStatus(
  area: AreaLike,
  completions: AreaProcessCompletion[],
  workDetails: WorkDetailLike[],
): AreaStatus {
  const wds = workDetailsOfSubtype(workDetails, area.work_subtype_id);
  if (wds.length === 0) {
    return "未着手";
  }
  const done = wds.filter((wd) => completions.some((c) => c.area_id === area.id && c.work_detail_id === wd.id)).length;
  if (done === 0) {
    return "未着手";
  }
  if (done >= wds.length) {
    return "完了";
  }
  return "進行中";
}

// ===== 工程ビュー(getAreaProcessViews 移植) =====

export interface AreaProcessView {
  workDetailId: number;
  workDetailName: string;
  sortOrder: number;
  mixRatioDefinitionId: number | null;
  materialId: number | null;
  // 主材料の計画使用量。工程別行(work_detail_id 付き)を優先し、無ければ旧 null 行(材料が一意工程のときのみ)へフォールバック。未入力は null。
  plannedMainQty: number | null;
  // 旧 null 行が同一材料の複数工程で共有され、工程へ一意に割り当てられないケース(詳細画面で警告表示する)。
  // このとき plannedMainQty は null だが、材料単位の旧計画数量合計を legacyMaterialQty に参考表示する。
  ambiguousLegacyQty: boolean;
  legacyMaterialQty: number | null;
  // メーカー参考値(閲覧専用)。
  referenceStandardUsage: number | null;
  referenceStandardUsageUnit: string | null;
  isCompleted: boolean;
  completedAt: string | null;
  constructionId: string | null;
}

export function getAreaProcessViews(
  area: AreaLike,
  completions: AreaProcessCompletion[],
  db: {
    workDetails: WorkDetailLike[];
    mixRatioDefinitions: MixRatioDefLike[];
    materials: MaterialLike[];
    plannedMaterials: PlannedMaterialLike[];
  },
): AreaProcessView[] {
  const wds = workDetailsOfSubtype(db.workDetails, area.work_subtype_id);
  const areaPlanned = db.plannedMaterials.filter((p) => p.deleted_at == null && p.construction_plan_area_id === area.id);

  // 各工程の主材料 material_id を先に解決し、「その材料を主材料とする工程数」を数える。
  // 旧 null 行のフォールバックは、この工程数が 1(一意)のときだけ許可する。
  const materialIdByWd = new Map<number, number>();
  const wdCountByMaterial = new Map<number, number>();
  for (const wd of wds) {
    const mixDef = wd.mix_ratio_definition_id == null
      ? undefined
      : db.mixRatioDefinitions.find((m) => m.deleted_at == null && m.id === wd.mix_ratio_definition_id);
    const mid = mixDef?.material_id ?? null;
    if (mid != null) {
      materialIdByWd.set(wd.id, mid);
      wdCountByMaterial.set(mid, (wdCountByMaterial.get(mid) ?? 0) + 1);
    }
  }

  return wds.map((wd, idx) => {
    const materialId = materialIdByWd.get(wd.id) ?? null;
    const mat = materialId == null ? undefined : db.materials.find((m) => m.id === materialId);
    const matched = completions.find((c) => c.area_id === area.id && c.work_detail_id === wd.id);

    let plannedMainQty: number | null = null;
    let ambiguousLegacyQty = false;
    let legacyMaterialQty: number | null = null;
    if (materialId != null) {
      const directRow = areaPlanned.find((p) => (p.work_detail_id ?? null) === wd.id && p.material_id === materialId);
      const legacyRows = areaPlanned.filter((p) => (p.work_detail_id ?? null) === null && p.material_id === materialId);
      const legacySum = legacyRows.reduce((s, p) => s + p.planned_qty, 0);
      const isUniqueForMaterial = (wdCountByMaterial.get(materialId) ?? 0) === 1;
      if (directRow !== undefined) {
        plannedMainQty = directRow.planned_qty;
      } else if (legacyRows.length > 0 && isUniqueForMaterial) {
        plannedMainQty = legacySum;
      } else if (legacyRows.length > 0) {
        ambiguousLegacyQty = true;
        legacyMaterialQty = legacySum;
      }
    }

    return {
      workDetailId: wd.id,
      workDetailName: wd.name,
      sortOrder: idx + 1,
      mixRatioDefinitionId: wd.mix_ratio_definition_id,
      materialId: materialId,
      plannedMainQty: plannedMainQty,
      ambiguousLegacyQty: ambiguousLegacyQty,
      legacyMaterialQty: legacyMaterialQty,
      referenceStandardUsage: mat?.standard_usage ?? null,
      referenceStandardUsageUnit: mat?.standard_usage_unit ?? null,
      isCompleted: matched != null,
      completedAt: matched?.completed_at ?? null,
      constructionId: matched?.construction_id ?? null,
    };
  });
}

// ===== 進捗(工程完了数/全数)集計 =====

export function aggregateProgress(
  areas: AreaLike[],
  completions: AreaProcessCompletion[],
  workDetails: WorkDetailLike[],
): { completed: number; total: number } {
  let completed = 0;
  let total = 0;
  for (const area of areas) {
    const wds = workDetailsOfSubtype(workDetails, area.work_subtype_id);
    total += wds.length;
    for (const wd of wds) {
      const done = completions.some((c) => c.area_id === area.id && c.work_detail_id === wd.id);
      if (done) {
        completed += 1;
      }
    }
  }
  return { completed, total };
}

// ===== 数量集計(aggregateAreaQuantities 移植) =====

export interface AreaQuantityAggregate {
  planCount: number;        // 計画施工箇所数 (= areas.length)
  completedCount: number;   // 種別配下の全工程が完了した area 数
  plannedQty: number;       // 計画数量合計
  actualQty: number;        // 実績数量合計(primary 完了で全量、未設定は工程比率按分)
  remainingQty: number;     // plannedQty - actualQty
  unit: string;             // subtype 単一なら unit、混在なら空文字
  mixedUnits: boolean;      // 単位混在(true なら数量は "-" 表示が望ましい)
}

export function aggregateAreaQuantities(
  areas: AreaLike[],
  completions: AreaProcessCompletion[],
  db: {
    workSubtypes: WorkSubtypeLike[];
    workDetails: WorkDetailLike[];
  },
): AreaQuantityAggregate {
  const bySubtype = new Map<number, AreaLike[]>();
  const noSubtype: AreaLike[] = [];
  for (const a of areas) {
    if (a.work_subtype_id == null) {
      noSubtype.push(a);
      continue;
    }
    const arr = bySubtype.get(a.work_subtype_id) ?? [];
    arr.push(a);
    bySubtype.set(a.work_subtype_id, arr);
  }

  let planCount = 0;
  let completedCount = 0;
  let plannedSum = 0;
  let actualSum = 0;
  const unitSet = new Set<string>();

  for (const entry of bySubtype) {
    const subtypeId = entry[0];
    const list = entry[1];
    const subtype = db.workSubtypes.find((s) => s.id === subtypeId && s.deleted_at == null);
    const unit = subtype?.unit ?? "";
    if (unit) {
      unitSet.add(unit);
    }
    const primaryWdId = subtype?.primary_work_detail_id ?? null;
    const wdsOfSubtype = db.workDetails.filter((d) => d.deleted_at == null && d.work_subtype_id === subtypeId);

    for (const area of list) {
      planCount += 1;
      const planned = area.planned_qty ?? 0;
      plannedSum += planned;

      const allCompleted = wdsOfSubtype.length > 0 && wdsOfSubtype.every((wd) =>
        completions.some((c) => c.area_id === area.id && c.work_detail_id === wd.id),
      );
      if (allCompleted) {
        completedCount += 1;
      }

      if (primaryWdId != null) {
        const primaryDone = completions.some((c) => c.area_id === area.id && c.work_detail_id === primaryWdId);
        if (primaryDone) {
          actualSum += planned;
        }
      } else if (wdsOfSubtype.length > 0) {
        const total = wdsOfSubtype.length;
        const done = wdsOfSubtype.filter((wd) =>
          completions.some((c) => c.area_id === area.id && c.work_detail_id === wd.id),
        ).length;
        actualSum += planned * (done / total);
      }
    }
  }

  // 種別未設定の area は件数のみ計上(数量・単位は不明として扱う)。
  for (const na of noSubtype) {
    planCount += 1;
    plannedSum += na.planned_qty ?? 0;
  }

  const single = Array.from(unitSet)[0] ?? "";
  return {
    planCount,
    completedCount,
    plannedQty: Math.round(plannedSum * 100) / 100,
    actualQty: Math.round(actualSum * 100) / 100,
    remainingQty: Math.round((plannedSum - actualSum) * 100) / 100,
    unit: single,
    mixedUnits: unitSet.size > 1,
  };
}
